import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { Clinic } from '../../../data/models/Clinic';
import { MatIconModule } from '@angular/material/icon';
import { CONSTANTS } from '../../../helper/constants';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-edit-clinic-dialog',
  imports: [ReactiveFormsModule, MatButtonModule, MatDialogModule, MatFormFieldModule, MatIconModule, MatInputModule],
  templateUrl: './edit-clinic-dialog.component.html',
  styleUrl: './edit-clinic-dialog.component.scss'
})
export class EditClinicDialogComponent implements OnInit, OnDestroy {
  form!: FormGroup
  blockTypeName: string
  clinic: Clinic
  selectedFile: File | null = null
  logoPreviewUrl: string | null = null
  fileError: string | null = null
  private blobUrl: string | null = null
  private readonly maxFileSize = 3 * 1024 * 1024

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<EditClinicDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { blockTypeName: string, clinic: Clinic }
  ) {
    this.blockTypeName = data.blockTypeName
    this.clinic = data.clinic
  }

  ngOnInit(): void {
    switch (this.blockTypeName) {
      case 'address':
        this.form = this.fb.group({
          street: [this.clinic.street],
          postal_code: [this.clinic.postal_code],
          city: [this.clinic.city]
        })
        break
      case 'phone':
        this.form = this.fb.group({
          phone: [this.clinic.phone]
        })
        break
      case 'website':
        this.form = this.fb.group({
          website: [this.clinic.website]
        })
        break
      default:
        this.form = this.fb.group({})
        break
    }

    if (this.blockTypeName === 'logo' && this.clinic.logo_path) {
      if (this.clinic.logo_path.startsWith('blob:')) {
        this.logoPreviewUrl = this.clinic.logo_path
      } else {
        this.logoPreviewUrl = `${environment.url}/${environment.backendVersion}/clinic/media/${this.clinic.logo_path}`
      }
    }
  }

  get dialogTitle(): string {
    switch (this.blockTypeName) {
      case 'logo': return 'Logo ändern'
      case 'address': return 'Adresse bearbeiten'
      case 'phone': return 'Rufnummer bearbeiten'
      case 'website': return 'Website bearbeiten'
      default: return 'Bearbeiten'
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement
    if (!input.files?.length) return

    const file = input.files[0]
    this.fileError = null

    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      this.fileError = 'Nur JPG und PNG Dateien sind erlaubt.'
      return
    }

    if (file.size > this.maxFileSize) {
      this.fileError = CONSTANTS.fileSizeExceededError
      return
    }

    this.selectedFile = file
    if (this.blobUrl) URL.revokeObjectURL(this.blobUrl)
    this.blobUrl = URL.createObjectURL(file)
    this.logoPreviewUrl = this.blobUrl
  }

  ngOnDestroy(): void {
    if (this.blobUrl) URL.revokeObjectURL(this.blobUrl)
  }

  get isValid(): boolean {
    if (this.blockTypeName === 'logo') return !!this.selectedFile
    return this.form.valid
  }

  save(): void {
    if (!this.isValid) return

    if (this.blockTypeName === 'logo') {
      this.dialogRef.close({ type: 'logo_upload', file: this.selectedFile })
    } else {
      this.dialogRef.close({ type: 'clinic_update', payload: this.form.value })
    }
  }
}
